import { useEffect, useState } from 'preact/hooks';
import type { PageProps } from '../app';
import type { LogClient } from '../core/log/client';
import { href, withFrom } from '../router';
import { Badge } from '../ui/components/Badge';
import { STATE_TONE } from '../ui/components/LogBadges';
import { PROGRAM_LABEL } from '../core/loglist/sources';
import { ErrorBox } from '../ui/components/ErrorBox';
import { Hash } from '../ui/components/Hash';
import { Icon } from '../ui/components/Icon';
import { LogLayout } from '../ui/components/LogLayout';
import { Card } from '../ui/components/Section';
import { Spinner } from '../ui/components/Spinner';
import { TimeStamp } from '../ui/components/TimeStamp';
import { fmtInt, fmtRelative } from '../ui/format';
import { useAsync } from '../ui/hooks/useAsync';
import { useLogClient } from '../ui/hooks/useLogClient';
import { activeProxyUrl, retryNotices } from '../ui/state/transport';
import { refreshTreeHead, treeHeads } from '../ui/state/treeHeads';
import { logPath } from '../ui/nav';
import { ExportMenu } from '../ui/components/Export';
import { pemEncode } from '../core/bytes/encoding';

export function NotFoundLog({ id }: { id: string }) {
  return (
    <main class="content">
      <h1>Unknown log</h1>
      <p>No log with id <code>{id}</code> is in the current log list or your custom logs.</p>
      <a class="btn" href={href('/logs')}><Icon name="arrowLeft" /> All logs</a>
    </main>
  );
}

export function LoadingPage() {
  return (
    <main class="content"><Spinner label="Loading log lists…" /></main>
  );
}

/** Overview tab: tree head, log facts, roots. */
export function LogPage({ params }: PageProps) {
  const { log, client, loading } = useLogClient(params);
  if (loading) return <LoadingPage />;
  if (!log) return <NotFoundLog id={`${params.op}/${params.log}`} />;
  return (
    <LogLayout log={log} {...(client ? { client } : {})} tab="overview">
      {client && <Overview client={client} />}
    </LogLayout>
  );
}

function Overview({ client }: { client: LogClient }) {
  const log = client.log;
  const th = treeHeads.value[log.id];
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const notice = retryNotices.value[log.id];
  const load = (force: boolean) => {
    setBusy(true);
    setError(undefined);
    refreshTreeHead(client, force).catch(setError).finally(() => setBusy(false));
  };
  const proxy = activeProxyUrl.value; // a blocked fetch re-runs as soon as the user turns the proxy on
  useEffect(() => load(false), [client, proxy]);
  return (
    <div class="stack">
      <Card
        title={<><Icon name="tree" /> {client.kind === 'tiled' ? 'Checkpoint' : 'Signed tree head'}</>}
        actions={
          <>
            {th && <button type="button" class="btn btn-sm btn-ghost" onClick={() => setShowRaw((v) => !v)}>{showRaw ? 'Hide raw' : 'Raw'}</button>}
            <button type="button" class="btn btn-sm" onClick={() => load(true)} disabled={busy}><Icon name="refresh" /> {busy ? 'Refreshing…' : 'Refresh'}</button>
          </>
        }
        foot={
          th ? (
            <>
              Fetched <TimeStamp ms={th.fetchedAt} /> from <code>{new URL(client.kind === 'tiled' ? log.monitoringUrl : log.url).host}</code>
              {client.kind === 'tiled' ? ' (monitoring prefix)' : ''}
              {th.origin && (
                <>
                  {' '}· checkpoint origin line (the log's identity, not a fetch target): <code>{th.origin}</code>
                </>
              )}
            </>
          ) : undefined
        }
      >
        {notice && <div class="notice">Rate limited by the log — retrying in {Math.round(notice.delayMs / 1000)}s (attempt {notice.attempt})…</div>}
        {error !== undefined && <ErrorBox error={error} title="Could not fetch the tree head" />}
        {!th && busy && <Spinner label="Fetching the tree head…" />}
        {th && (
          <>
            <div class="stats">
              <div class="stat"><div class="label">Tree size</div><div class="value">{fmtInt(th.treeSize)}</div><div class="sub">entries</div></div>
              <div class="stat"><div class="label">Timestamp</div><div class="value">{th.timestamp !== undefined ? fmtRelative(th.timestamp) : '—'}</div><div class="sub">{th.timestamp !== undefined ? new Date(th.timestamp).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : ''}</div></div>
              <div class="stat">
                <div class="label">Signature</div>
                <div class="value">
                  {th.signature.status === 'verified' && <span class="verdict ok"><Icon name="check" /> verified</span>}
                  {th.signature.status === 'failed' && <span class="verdict err"><Icon name="x" /> invalid</span>}
                  {th.signature.status === 'no-key' && <span class="verdict warn">no key</span>}
                </div>
                <div class="sub">{th.signature.status === 'verified' ? "with the log's public key" : th.signature.error ?? 'add the key to verify'}</div>
              </div>
            </div>
            <dl class="kv" style="margin-top:0.9rem">
              <dt>Root hash</dt>
              <dd><Hash value={th.rootHash} /></dd>
            </dl>
            {showRaw && <pre class="raw">{th.raw}</pre>}
          </>
        )}
      </Card>

      <div class="grid-4">
        <a class="card action-card" href={logPath(log, 'entries')}>
          <span class="ico"><Icon name="list" /></span>
          <span><b>Browse entries</b><span>Page through the log, decode certificates and chains.</span></span>
        </a>
        <a class="card action-card" href={logPath(log, 'inclusion')}>
          <span class="ico"><Icon name="shield" /></span>
          <span><b>Verify an inclusion proof</b><span>Show that an entry is in the tree behind this head.</span></span>
        </a>
        <a class="card action-card" href={logPath(log, 'consistency')}>
          <span class="ico"><Icon name="tree" /></span>
          <span><b>Verify consistency</b><span>Prove the log only ever appended between two sizes.</span></span>
        </a>
        <a class="card action-card" href={withFrom(href('/submit', { log: log.id }))}>
          <span class="ico"><Icon name="upload" /></span>
          <span><b>Submit a chain to this log</b><span>add-chain / add-pre-chain, with the SCT verified and saved.</span></span>
        </a>
      </div>

      <Card title={<><Icon name="info" /> About this log</>}>
        <dl class="kv">
          <dt>Operator</dt><dd>{log.operator}</dd>
          {log.kind === 'tiled' ? (
            <>
              <dt>Monitoring prefix</dt><dd><code>{log.monitoringUrl}</code></dd>
              <dt>Submission prefix</dt><dd><code>{log.submissionUrl}</code></dd>
            </>
          ) : (
            <><dt>API base</dt><dd><code>{log.url}ct/v1/</code></dd></>
          )}
          <dt>Log ID</dt>
          <dd>{log.logId ? <div class="row"><Hash value={log.logId} /><span class="tiny muted">base64 {log.logId.toBase64()}</span></div> : <span class="muted">unknown (no key configured)</span>}</dd>
          {log.key && (
            <>
              <dt>Public key</dt>
              <dd>
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setShowKey((v) => !v)}>{showKey ? 'Hide' : 'Show'} SPKI (base64)</button>
                {showKey && <pre class="raw">{log.key.toBase64()}</pre>}
              </dd>
            </>
          )}
          {log.temporalInterval && <><dt>Accepts certificates expiring</dt><dd>{log.temporalInterval.start.slice(0, 10)} → {log.temporalInterval.end.slice(0, 10)}</dd></>}
          {log.mmd !== undefined && <><dt>Maximum merge delay</dt><dd>{log.mmd >= 3600 ? `${log.mmd / 3600} h` : `${log.mmd} s`}</dd></>}
          <dt>Program status</dt>
          <dd>
            <ul class="plain stack" style="gap:0.25rem">
              {log.programs.length === 0 && <li class="muted">Not in any program's list (custom log)</li>}
              {log.programs.map((p) => (
                <li key={p.program} class="row">
                  <Badge tone={STATE_TONE[p.state]}>{PROGRAM_LABEL[p.program]} · {p.state}</Badge>
                  {p.stateTimestamp && <span class="small muted">since {p.stateTimestamp.slice(0, 10)}</span>}
                  {p.description && <span class="small muted">listed as "{p.description}"</span>}
                </li>
              ))}
            </ul>
          </dd>
          <dt>Accepted roots</dt>
          <dd><RootsTool client={client} /></dd>
        </dl>
      </Card>
    </div>
  );
}

function RootsTool({ client }: { client: LogClient }) {
  const [wanted, setWanted] = useState(false);
  const roots = useAsync(async (signal) => (wanted ? client.getRoots(signal) : undefined), [wanted, client]);
  if (!wanted) return <button type="button" class="btn btn-sm" onClick={() => setWanted(true)}><Icon name="download" /> Load accepted roots</button>;
  if (roots.status === 'loading') return <Spinner label="Fetching get-roots…" />;
  if (roots.status === 'error') return <ErrorBox error={roots.error} title="Could not fetch roots" />;
  return (
    <span class="row">
      <span>{fmtInt(roots.value?.length ?? 0)} root certificates</span>
      <ExportMenu label="Roots" formats={[{ label: 'PEM bundle', filename: `${client.log.description.replace(/\W+/g, '_')}-roots.pem`, mime: 'application/x-pem-file', hint: `${fmtInt(roots.value?.length ?? 0)} certificates`, text: () => (roots.value ?? []).map((d) => pemEncode(d)).join('') }]} />
    </span>
  );
}
